import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { Avatar, Botao, Esqueleto, Vazio } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { comprimirImagem } from '@/lib/imagens'
import { type ContextoPrograma, usePerfil } from '@/lib/sessao'
import { supabase, urlAssinada } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'

interface Mensagem {
  id: string
  turma_id: string
  user_id: string
  texto: string | null
  imagem_path: string | null
  criado_em: string
  autor: { nome: string | null; email: string | null } | null
}

const LIMITE_TEXTO = 500

/**
 * Comunidade — mural por turma (não conversa individual).
 *
 * Um mural compartilhado, não mensagens diretas: para uma turma de
 * poucas pessoas, é a forma mais simples de dar espaço para interação
 * sem construir (e depois precisar moderar) um sistema de chat inteiro.
 * Substitui dentro do app o "WhatsApp de comunidade" que a seção 1 da
 * especificação original previa ficar de fora.
 *
 * Mensagens chegam em tempo real via Supabase Realtime — sem isso,
 * ver a foto do prato de alguém exigiria recarregar a página.
 */
export function PaginaComunidade({ contexto }: { contexto: ContextoPrograma }) {
  const { matricula } = contexto
  const turmaId = matricula.turma_id
  const { data: perfil } = usePerfil()
  const clienteQuery = useQueryClient()

  const [texto, setTexto] = useState('')
  const [imagem, setImagem] = useState<File | null>(null)
  const [previaImagem, setPreviaImagem] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const fimDaLista = useRef<HTMLDivElement>(null)

  const chave = ['comunidade', turmaId] as const

  const { data: mensagens = [], isLoading } = useQuery({
    queryKey: chave,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mensagens_comunidade')
        .select('*, autor:perfis(nome, email)')
        .eq('turma_id', turmaId)
        .order('criado_em', { ascending: true })
        .limit(300)

      if (error) throw error
      return data as Mensagem[]
    },
  })

  useEffect(() => {
    const canal = supabase
      .channel(`comunidade-${turmaId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mensagens_comunidade',
          filter: `turma_id=eq.${turmaId}`,
        },
        () => void clienteQuery.invalidateQueries({ queryKey: ['comunidade', turmaId] }),
      )
      .subscribe()

    return () => void supabase.removeChannel(canal)
  }, [turmaId, clienteQuery])

  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ block: 'end' })
  }, [mensagens.length])

  function escolherImagem(evento: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0] ?? null
    setImagem(arquivo)
    setPreviaImagem(arquivo ? URL.createObjectURL(arquivo) : null)
    evento.target.value = ''
  }

  const enviar = useMutation({
    mutationFn: async () => {
      const textoLimpo = texto.trim()
      if (!textoLimpo && !imagem) return

      const { data: sessao } = await supabase.auth.getSession()
      const userId = sessao.session?.user.id
      if (!userId) throw new Error('Sessão expirada. Recarregue a página.')

      let imagemPath: string | null = null
      if (imagem) {
        const comprimida = await comprimirImagem(imagem)
        imagemPath = `${turmaId}/${crypto.randomUUID()}.webp`
        const { error: erroUpload } = await supabase.storage
          .from('comunidade')
          .upload(imagemPath, comprimida, { contentType: 'image/webp' })
        if (erroUpload) throw erroUpload
      }

      const { error } = await supabase.from('mensagens_comunidade').insert({
        turma_id: turmaId,
        user_id: userId,
        texto: textoLimpo || null,
        imagem_path: imagemPath,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setTexto('')
      setImagem(null)
      setPreviaImagem(null)
      setErro(null)
      void clienteQuery.invalidateQueries({ queryKey: chave })
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  })

  const remover = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('mensagens_comunidade').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: chave }),
  })

  const podeEnviar = (texto.trim().length > 0 || imagem) && !enviar.isPending

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-titulo text-2xl font-bold">Comunidade</h1>
        <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">{matricula.turma.nome}</p>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pb-40">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((n) => (
              <Esqueleto key={n} className="h-20" />
            ))}
          </div>
        ) : mensagens.length === 0 ? (
          <Vazio
            emoji="💬"
            titulo="O mural está vazio"
            descricao="Seja a primeira a compartilhar algo com a turma — uma foto do prato, uma vitória do dia, o que quiser."
          />
        ) : (
          <ul className="flex flex-col gap-4">
            {mensagens.map((mensagem) => (
              <LinhaMensagem
                key={mensagem.id}
                mensagem={mensagem}
                souEu={mensagem.user_id === perfil?.id}
                souAdmin={perfil?.papel === 'admin'}
                aoRemover={() => remover.mutate(mensagem.id)}
              />
            ))}
          </ul>
        )}
        <div ref={fimDaLista} />
      </div>

      {/* Fixo acima da navegação, e não sobre ela — barra tem safe-area
          própria e ~68px de altura visual. */}
      <div className="fixed inset-x-0 bottom-[68px] mx-auto max-w-md border-t border-[var(--borda)] bg-[var(--fundo)] px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
        {previaImagem && (
          <div className="mb-2 flex items-center gap-2">
            <img src={previaImagem} alt="" className="size-14 rounded-xl object-cover" />
            <button
              type="button"
              onClick={() => {
                setImagem(null)
                setPreviaImagem(null)
              }}
              className="text-xs font-medium text-pink underline"
            >
              Remover foto
            </button>
          </div>
        )}

        {erro && (
          <p role="alert" className="mb-2 text-xs text-pink">
            {erro}
          </p>
        )}

        <div className="flex items-end gap-2">
          <label className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[var(--cartao)] text-xl">
            {/* Sem `capture`: força a câmera e esconde a galeria. */}
            <input type="file" accept="image/*" className="sr-only" onChange={escolherImagem} />
            <span aria-hidden>📷</span>
            <span className="sr-only">Anexar foto</span>
          </label>

          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value.slice(0, LIMITE_TEXTO))}
            placeholder="Escreva algo para a turma…"
            rows={1}
            className="max-h-24 min-h-11 flex-1 resize-none rounded-2xl border-2 border-[var(--borda)] bg-[var(--cartao)] px-4 py-2.5 text-[15px] leading-snug"
          />

          <Botao
            tamanho="pequeno"
            disabled={!podeEnviar}
            carregando={enviar.isPending}
            onClick={() => enviar.mutate()}
            aria-label="Enviar"
            className="size-11 shrink-0 p-0"
          >
            ➤
          </Botao>
        </div>
      </div>
    </div>
  )
}

function LinhaMensagem({
  mensagem,
  souEu,
  souAdmin,
  aoRemover,
}: {
  mensagem: Mensagem
  souEu: boolean
  souAdmin: boolean
  aoRemover: () => void
}) {
  const { data: urlImagem } = useQuery({
    queryKey: ['comunidade-foto', mensagem.imagem_path],
    queryFn: () => urlAssinada('comunidade', mensagem.imagem_path as string, 30 * 60),
    enabled: Boolean(mensagem.imagem_path),
    staleTime: 25 * 60 * 1000,
  })

  const nomeAutor = mensagem.autor?.nome || mensagem.autor?.email || 'Alguém da turma'

  return (
    <li className={cn('flex gap-3 rounded-2xl p-3', souEu && 'bg-coral/5')}>
      <Avatar nome={mensagem.autor?.nome} email={mensagem.autor?.email} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-titulo text-sm font-semibold">
            {souEu ? 'Você' : nomeAutor}
          </span>
          <span className="shrink-0 text-[11px] text-[var(--tinta-apagada)]">
            {formatarData(mensagem.criado_em, "d/MM 'às' HH:mm")}
          </span>
        </div>

        {mensagem.texto && (
          <p className="mt-0.5 text-[15px] leading-relaxed whitespace-pre-wrap">
            {mensagem.texto}
          </p>
        )}

        {mensagem.imagem_path &&
          (urlImagem ? (
            <img
              src={urlImagem}
              alt=""
              className="mt-2 max-h-72 w-full rounded-2xl object-cover"
            />
          ) : (
            <Esqueleto className="mt-2 h-48 w-full" />
          ))}

        {(souEu || souAdmin) && (
          <button
            type="button"
            onClick={aoRemover}
            className="mt-1 text-[11px] font-medium text-[var(--tinta-apagada)] underline"
          >
            Remover
          </button>
        )}
      </div>
    </li>
  )
}
